import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Smile, Loader2, X } from "lucide-react";

const MOODS = [
  { id: "amazing", emoji: "🤩", label: "Increíble", color: "#22c55e" },
  { id: "happy", emoji: "😊", label: "Feliz", color: "#84cc16" },
  { id: "good", emoji: "🙂", label: "Bien", color: "#a3e635" },
  { id: "neutral", emoji: "😐", label: "Normal", color: "#facc15" },
  { id: "tired", emoji: "😴", label: "Cansado", color: "#fb923c" },
  { id: "stressed", emoji: "😰", label: "Estresado", color: "#f97316" },
  { id: "sad", emoji: "😢", label: "Triste", color: "#ef4444" },
  { id: "angry", emoji: "😤", label: "Enfadado", color: "#dc2626" },
];

export default function MoodPopup() {
  const [open, setOpen] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  
  const { token, user } = useAuth();
  const location = useLocation();

  // Check if mood was recorded today (single unified backend call)
  const checkMoodToday = async () => {
    if (!token || hasChecked) return;

    // Don't show on login/launch/public pages
    if (["/login", "/launch", "/bienvenida"].includes(location.pathname)) return;

    // Fast local guard: if the backend told us "no prompt" earlier today,
    // avoid a network round-trip on every route change.
    const today = new Date().toISOString().split("T")[0];
    const localSkip = localStorage.getItem("mood_skip_date");
    if (localSkip === today) {
      setHasChecked(true);
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bienestar/mood/should-prompt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.should_prompt) {
          setOpen(true);
        } else {
          // Persist "already done today" for this session to skip network
          localStorage.setItem("mood_skip_date", today);
        }
      }
    } catch (e) {
      console.error("Error checking mood:", e);
    }
    setHasChecked(true);
  };

  // Check on mount and route changes
  useEffect(() => {
    if (token && user) {
      // Small delay to let the page load after animation
      const timer = setTimeout(() => {
        checkMoodToday();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [token, user, location.pathname]);

  const handleSave = async () => {
    if (!selectedMood) {
      toast.error("Selecciona cómo te sientes");
      return;
    }
    
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bienestar/mood`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mood: selectedMood,
          note: note,
          date: today
        })
      });
      
      if (res.ok) {
        localStorage.setItem('mood_skip_date', today);
        toast.success("¡Gracias por compartir cómo te sientes! 💪");
        setOpen(false);
        setSelectedMood(null);
        setNote("");
      } else {
        toast.error("Error al guardar");
      }
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async () => {
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("mood_skip_date", today);
    setOpen(false);
    // Persist dismissal to Mongo so it's cross-device.
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bienestar/mood/dismiss`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* silent — localStorage already covers this device */ }
  };

  if (!token || !user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={false}>
      <DialogContent className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Smile className="w-6 h-6 text-yellow-500" />
            ¡Buenos días, {user?.full_name?.split(' ')[0]}!
          </DialogTitle>
          <DialogDescription>
            ¿Cómo te sientes hoy? Tu bienestar nos importa.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Mood Selection Grid */}
          <div className="grid grid-cols-4 gap-3">
            {MOODS.map(mood => (
              <button
                key={mood.id}
                type="button"
                onClick={() => setSelectedMood(mood.id)}
                className={`flex flex-col items-center p-3 rounded-xl transition-all duration-200 hover:scale-105
                  ${selectedMood === mood.id 
                    ? "bg-primary/20 ring-2 ring-primary shadow-lg scale-105" 
                    : "bg-muted hover:bg-muted/80"
                  }
                `}
              >
                <span className="text-3xl mb-1">{mood.emoji}</span>
                <span className="text-xs font-medium">{mood.label}</span>
              </button>
            ))}
          </div>
          
          {/* Note field - only show after mood selection */}
          {selectedMood && (
            <div className="space-y-2 animate-in slide-in-from-bottom-2">
              <label className="text-sm font-medium text-muted-foreground">
                ¿Algo que quieras compartir? (opcional)
              </label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Hoy me siento así porque..."
                rows={2}
                className="resize-none"
              />
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleDismiss} className="text-muted-foreground">
            <X className="w-4 h-4 mr-2" />
            Ahora no
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !selectedMood}
            className="min-w-[120px]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Smile className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
